import axios from 'axios';

const API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
// Using Gemini Flash Latest which ensures we use the best available flash model
const API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-flash-latest:generateContent';

/**
 * Helper to retry an async function with exponential backoff
 */
const retryOperation = async (operation, retries = 3, delay = 1000) => {
  try {
    return await operation();
  } catch (error) {
    if (retries <= 0) throw error;
    
    // Check if it's a transient error (503 Service Unavailable or 429 Too Many Requests)
    if (error.response && (error.response.status === 503 || error.response.status === 429)) {
      console.log(`Retrying AI request... Attempts left: ${retries}. Waiting ${delay}ms.`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return retryOperation(operation, retries - 1, delay * 2);
    }
    
    throw error;
  }
};

/**
 * Generates text using Google Gemini API or falls back to simulation if no key is provided.
 * @param {string} topic - The topic to generate text about.
 * @param {string} recipientName - The name of the recipient (User or App name).
 * @returns {Promise<string>} - The generated text.
 */
export const generateAlertText = async (topic, recipientName) => {
  // Fallback to simulation if no API Key is configured
  if (!API_KEY) {
    console.warn('REACT_APP_GEMINI_API_KEY not found in environment variables. Using simulation.');
    return new Promise((resolve) => {
      setTimeout(() => {
        const simulatedText = `Prezado(a) ${recipientName || 'Cliente'},\n\nReferente ao assunto "${topic}", gostaríamos de informar que analisamos sua solicitação e estamos processando as informações necessárias.\n\nCaso tenha dúvidas adicionais, não hesite em nos contatar.\n\nAtenciosamente,\nEquipe de Suporte.`;
        resolve(simulatedText);
      }, 1500);
    });
  }

  try {
    const prompt = `Escreva APENAS o corpo de uma mensagem formal e profissional de alerta/notificação administrativa para um usuário chamado "${recipientName || 'Usuário'}".
    
    O assunto/tópico é: "${topic}".
    
    Regras OBRIGATÓRIAS:
    1. NÃO inclua introduções como "Aqui está uma sugestão", "Assunto:", ou "Prezado". Comece a mensagem diretamente.
    2. O tom deve ser de suporte/administrativo, educado e direto.
    3. Máximo de 3 parágrafos curtos.
    4. Em Português do Brasil.
    5. NÃO adicione assinatura no final (será adicionada automaticamente).`;

    const makeRequest = () => axios.post(`${API_URL}?key=${API_KEY}`, {
      contents: [{
        parts: [{
          text: prompt
        }]
      }]
    });

    const response = await retryOperation(makeRequest);

    if (
      response.data && 
      response.data.candidates && 
      response.data.candidates.length > 0 &&
      response.data.candidates[0].content &&
      response.data.candidates[0].content.parts &&
      response.data.candidates[0].content.parts.length > 0
    ) {
      let generatedText = response.data.candidates[0].content.parts[0].text.trim();
      
      // Remove possible subject line if AI ignored instructions
      generatedText = generatedText.replace(/^Assunto:.*\n+/i, '');
      generatedText = generatedText.replace(/^Subject:.*\n+/i, '');
      
      // Ensure proper salutation
      const salutation = `Prezado(a) ${recipientName || 'Usuário'},`;
      if (!generatedText.startsWith('Prezado')) {
        generatedText = `${salutation}\n\n${generatedText}`;
      }

      // Append the specific signature
      const signature = `\n\nAtenciosamente,\n\nLuiz\nCriaApp.`;
      
      // Remove any existing signature the AI might have added
      generatedText = generatedText.replace(/\n\nAtenciosamente,[\s\S]*$/, '');
      
      return generatedText + signature;
    } else {
      throw new Error('Resposta inválida da API do Gemini.');
    }
  } catch (error) {
    console.error('Erro ao gerar texto com IA:', error);
    // Fallback in case of error
    return `Erro ao gerar texto com IA. Por favor, tente novamente ou escreva manualmente.\n\nErro: ${error.message}`;
  }
};

/**
 * Generates store app store optimization metadata (ASO).
 * @param {string} appName - The name of the application.
 * @param {string} category - The app category (e.g. Delivery, E-commerce).
 * @param {string} keywords - Keywords associated with the app.
 * @param {string} platform - The target platform (e.g., Lojas de Apps, Instagram, Facebook, WhatsApp).
 * @returns {Promise<string>} - The generated ASO title, subtitle and description or social media post.
 */
export const generateAsoText = async (appName, category, keywords, platform = 'Lojas de Apps') => {
  if (!API_KEY) {
    console.warn('REACT_APP_GEMINI_API_KEY not found. Using ASO simulation.');
    return new Promise((resolve) => {
      setTimeout(() => {
        const simulatedText = `[TÍTULO DE BUSCA (ASO)]
${appName || 'Meu App'} - Compre Online

[SUBTÍTULO DA LOJA]
Sua loja virtual de ${category || 'Produtos'} rápida e segura

[DESCRIÇÃO OTIMIZADA]
Baixe o aplicativo oficial do ${appName || 'Meu App'} e tenha a melhor experiência de compra diretamente no seu celular!

Principais recursos:
• Notificações Push exclusivas com promoções em tempo real
• Checkout nativo e seguro para compras rápidas
• Acesso rápido a todo o catálogo de ${category || 'Produtos'}
• Suporte completo integrado no aplicativo

Palavras-chave otimizadas: ${keywords || 'compras, loja, app, mobile'}`;
        
        let socialSimulatedText = `[POST PARA ${platform.toUpperCase()}]\nOlá pessoal! 🎉 Já conhecem o ${appName || 'Meu App'}? A sua nova forma favorita de aproveitar ${category || 'Produtos'}!\n\nBaixe agora mesmo no link da bio e aproveite! 📲✨\n\n#${appName.replace(/\s+/g, '') || 'MeuApp'} #app #${category.replace(/\s+/g, '') || 'categoria'} #${keywords ? keywords.split(',')[0].trim() : 'novidade'}`;

        resolve(platform === 'Lojas de Apps' ? simulatedText : socialSimulatedText);
      }, 1500);
    });
  }

  try {
    let prompt = `Gere os metadados de otimização de loja (ASO) para o aplicativo com as seguintes informações:
    Nome do App: "${appName}"
    Categoria: "${category}"
    Palavras-chave: "${keywords}"
    
    Escreva um output formatado contendo:
    1. Título de Busca (máximo 30 caracteres)
    2. Subtítulo da Loja (máximo 30 caracteres)
    3. Descrição Otimizada (curta, com benefícios claros e bullet points para os recursos)
    
    Regras:
    - Em Português do Brasil.
    - Foco em conversão e busca orgânica (SEO de aplicativos).
    - Evite termos repetidos de forma não natural.`;

    if (platform !== 'Lojas de Apps') {
      prompt = `Crie um post engajador e chamativo para a rede social "${platform}", focado em divulgar o aplicativo com as seguintes informações:
      Nome do App: "${appName}"
      Categoria: "${category}"
      Palavras-chave: "${keywords}"

      Regras:
      - A linguagem deve ser adequada para o ${platform} (ex: emojis, chamadas para ação, tom amigável).
      - Inclua as hashtags mais relevantes no final.
      - Foco em conversão (levar a pessoa a baixar o app).
      - Em Português do Brasil.`;
    }

    const makeRequest = () => axios.post(`${API_URL}?key=${API_KEY}`, {
      contents: [{
        parts: [{
          text: prompt
        }]
      }]
    });

    const response = await retryOperation(makeRequest);

    if (
      response.data && 
      response.data.candidates && 
      response.data.candidates.length > 0 &&
      response.data.candidates[0].content &&
      response.data.candidates[0].content.parts &&
      response.data.candidates[0].content.parts.length > 0
    ) {
      return response.data.candidates[0].content.parts[0].text.trim();
    } else {
      throw new Error('Resposta inválida da API do Gemini.');
    }
  } catch (error) {
    console.error('Erro ao gerar ASO com IA:', error);
    return `Erro ao gerar metadados ASO. Por favor, tente novamente.\n\nDetalhes: ${error.message}`;
  }
};
